import React, { useState, useEffect } from 'react';
import {
  MessageSquare, Users, CheckCircle, Clock,
  TrendingUp, Calendar, RefreshCw, Phone,
} from 'lucide-react';
import { StatsCard, HealthStatus } from '../../components/dashboard';
import { Card, Loading, Badge } from '../../components/common';
import useAuth from '../../hooks/useAuth';
import conversationsService from '../../services/api/conversations.service';
import tenantsService from '../../services/api/tenants.service';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const formatSafeDate = (dateString) => {
  try {
    if (!dateString) return 'Data não disponível';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Data inválida';
    return formatDistanceToNow(date, { addSuffix: true, locale: ptBR });
  } catch { return 'Data inválida'; }
};

const getConversationStatus = (conversation) => {
  let currentStep = '';
  if (conversation.context) {
    currentStep =
      conversation.context.current_step ||
      conversation.context.currentStep ||
      conversation.context.step ||
      conversation.context.stepId ||
      conversation.context.step_id || '';
  }
  if (!currentStep && conversation) {
    currentStep = conversation.current_step || conversation.currentStep || conversation.step || '';
  }
  const stepStr = String(currentStep).toLowerCase();
  if (stepStr.includes('perguntar_reinicio')) return 'closed';
  if (conversation.context?.flow_finished === true || conversation.context?.flow_finished === 'true') return 'closed';
  if (conversation.context?.restarted_by_user === true || conversation.context?.restarted_by_user === 'true') return 'open';
  return conversation.status || 'open';
};

const TenantOverview = () => {
  const { user } = useAuth();
  const tenantId = user?.tenantId || user?.id;

  const [loading, setLoading]     = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [recentConversations, setRecentConversations] = useState([]);
  const [stats, setStats] = useState({
    totalConversations: 0, activeConversations: 0,
    resolvedConversations: 0, resolvedPercentage: 0,
  });

  useEffect(() => { if (tenantId) loadData(); }, [tenantId]);

  const loadData = async () => {
    try {
      setLoading(true);
      try {
        const tenant = await tenantsService.getById(tenantId);
        setTenantInfo(tenant);
      } catch (err) { console.error('Erro ao carregar tenant:', err); }

      const allConversations = await conversationsService.getAllConversations(tenantId, { status: 'all' });
      const openList   = allConversations.filter((c) => getConversationStatus(c) === 'open');
      const closedList = allConversations.filter((c) => getConversationStatus(c) === 'closed');
      const total      = allConversations.length;
      const resolvedPercentage = total > 0 ? Math.round((closedList.length / total) * 100) : 0;
      setStats({ totalConversations: total, activeConversations: openList.length, resolvedConversations: closedList.length, resolvedPercentage });
      setRecentConversations(openList.slice(0, 5));
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => { setRefreshing(true); await loadData(); setRefreshing(false); };

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Bom dia';
    if (h < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Loading size="lg" text="Carregando dashboard..." />
      </div>
    );
  }

  return (
    <div className="space-y-5">

      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-500 via-primary-400 to-primary-600 p-6 text-white shadow-lg">
        <div className="pointer-events-none absolute -right-16 -top-16 w-64 h-64 rounded-full bg-white/5" />
        <div className="pointer-events-none absolute -right-4 -bottom-8 w-40 h-40 rounded-full bg-white/5" />

        <div className="relative flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <p className="text-primary-100 text-sm font-medium mb-1">Painel de Automações</p>
            <h1 className="text-2xl font-bold font-display">
              {getGreeting()}, {user?.name?.split(' ')[0] || tenantInfo?.name || 'Usuário'}
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="flex items-center gap-2 bg-white/15 border border-white/20 rounded-lg px-3 py-2">
              <Calendar className="w-3.5 h-3.5 text-white/70" />
              <span className="text-sm font-medium hidden sm:block">
                {new Date().toLocaleDateString('pt-BR', { weekday: 'short', day: 'numeric', month: 'short' })}
              </span>
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 bg-white/15 hover:bg-white/25 rounded-lg border border-white/20 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
          {[
            { label: 'Total de Conversas', value: stats.totalConversations },
            { label: 'Taxa de Resolução',  value: `${stats.resolvedPercentage}%` },
            { label: 'Abertas',            value: stats.activeConversations },
            { label: 'Resolvidas',         value: stats.resolvedConversations },
          ].map(({ label, value }) => (
            <div key={label} className="bg-white/10 backdrop-blur-sm rounded-xl p-3.5 border border-white/10">
              <p className="text-white/70 text-xs mb-1">{label}</p>
              <p className="text-2xl font-bold font-display">{value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Tenant badge */}
      {tenantInfo && (
        <Card padding="sm">
          <Card.Body className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{tenantInfo.name}</p>
                <p className="text-xs text-slate-400 dark:text-slate-500">ID: {tenantInfo.id}</p>
              </div>
              <div className="flex gap-2">
                <Badge variant={tenantInfo.isActive ? 'success' : 'danger'} dot>
                  {tenantInfo.isActive ? 'Ativo' : 'Inativo'}
                </Badge>
                <Badge variant={tenantInfo.botPaused ? 'warning' : 'primary'}>
                  {tenantInfo.botPaused ? 'Bot Pausado' : 'Bot Ativo'}
                </Badge>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard title="Total de Conversas" value={stats.totalConversations} icon={MessageSquare} subtitle="todas as conversas" color="primary" />
        <StatsCard title="Conversas Ativas"   value={stats.activeConversations}   icon={Clock}         subtitle="em andamento"   color="warning"
          percentage={stats.totalConversations > 0 ? Math.round((stats.activeConversations / stats.totalConversations) * 100) : 0} />
        <StatsCard title="Resolvidas"          value={stats.resolvedConversations} icon={CheckCircle}   subtitle="fechadas"       color="success" percentage={stats.resolvedPercentage} />
        <StatsCard title="Taxa de Resolução"   value={`${stats.resolvedPercentage}%`} icon={TrendingUp} subtitle="conversas resolvidas" color="info" percentage={stats.resolvedPercentage} />
      </div>

      {/* Middle row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card>
          <Card.Header>
            <Card.Title>Conversas por Status</Card.Title>
            <Card.Description>Distribuição das conversas atuais</Card.Description>
          </Card.Header>
          <Card.Body className="space-y-4">
            {[
              { label: 'Resolvidas',    pct: stats.resolvedPercentage, bar: 'from-emerald-400 to-emerald-500', count: stats.resolvedConversations },
              { label: 'Em Andamento',  pct: stats.totalConversations > 0 ? Math.round((stats.activeConversations / stats.totalConversations) * 100) : 0, bar: 'from-amber-400 to-amber-500', count: stats.activeConversations },
            ].map(({ label, pct, bar, count }) => (
              <div key={label}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 dark:text-slate-500">{count}</span>
                    <span className="text-sm font-bold text-slate-900 dark:text-slate-100">{pct}%</span>
                  </div>
                </div>
                <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div className={`h-full bg-gradient-to-r ${bar} rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            ))}
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>
            <Card.Title>Resumo Geral</Card.Title>
            <Card.Description>Estatísticas principais</Card.Description>
          </Card.Header>
          <Card.Body className="space-y-2.5">
            {[
              { icon: MessageSquare, bg: 'bg-primary-50 dark:bg-primary-900/25', color: 'text-primary-600 dark:text-primary-400', label: 'Total de Conversas', sub: 'Todas registradas', value: stats.totalConversations },
              { icon: CheckCircle,   bg: 'bg-emerald-50 dark:bg-emerald-900/25', color: 'text-emerald-600 dark:text-emerald-400', label: 'Resolvidas',          sub: 'Fechadas com sucesso', value: stats.resolvedConversations },
              { icon: Clock,         bg: 'bg-amber-50 dark:bg-amber-900/25',     color: 'text-amber-600 dark:text-amber-400',    label: 'Abertas',             sub: 'Aguardando atendimento', value: stats.activeConversations },
            ].map(({ icon: Icon, bg, color, label, sub, value }) => (
              <div key={label} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 ${bg} rounded-lg flex items-center justify-center`}>
                    <Icon className={`w-4 h-4 ${color}`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{label}</p>
                    <p className="text-xs text-slate-400 dark:text-slate-500">{sub}</p>
                  </div>
                </div>
                <p className="text-xl font-bold text-slate-900 dark:text-slate-100 font-display">{value}</p>
              </div>
            ))}
          </Card.Body>
        </Card>

        <HealthStatus />
      </div>

      {/* Recent conversations */}
      <Card>
        <Card.Header>
          <div className="flex items-center justify-between">
            <div>
              <Card.Title>Conversas Recentes</Card.Title>
              <Card.Description>Últimas {recentConversations.length} conversas abertas</Card.Description>
            </div>
            <TrendingUp className="w-4 h-4 text-primary-500" />
          </div>
        </Card.Header>
        <Card.Body>
          {recentConversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10">
              <div className="w-12 h-12 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mb-3">
                <MessageSquare className="w-5 h-5 text-slate-300 dark:text-slate-600" />
              </div>
              <p className="text-sm text-slate-400 dark:text-slate-500">Nenhuma conversa ativa no momento</p>
            </div>
          ) : (
            <div className="space-y-2">
              {recentConversations.map((conversation) => (
                <div
                  key={conversation.id}
                  className="flex items-center justify-between p-3.5 bg-slate-50 dark:bg-slate-900/50 hover:bg-slate-100 dark:hover:bg-slate-800/60 rounded-xl transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 bg-primary-50 dark:bg-primary-900/25 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Users className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">
                        {conversation.profile_name || conversation.context?.nome || 'Sem nome'}
                      </p>
                      <div className="flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500">
                        <Phone className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{conversation.wa_id || conversation.lead?.phone}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {conversation.bot_paused && (
                      <Badge variant="warning" size="sm">Bot Pausado</Badge>
                    )}
                    <p className="text-xs text-slate-400 dark:text-slate-500 hidden sm:block">
                      {formatSafeDate(conversation.created_at)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default TenantOverview;
